import { useCallback, useEffect, useRef, useState } from 'react';
import { demeterDashboardsApi } from '../../../Apis/Apis';
import applicationConstants from '../../../Core/Utility/ApplicationConstants';
import {
    DemeterDashboardModel,
    DemeterDashboardWidgetModel,
    DemeterDashboardWidgetPageElementModel,
    DemeterPageElementModel,
    UpsertDemeterDashboardRequest,
} from '../../../Generated/Raven-Demeter';
import useRegionCommodityMenuSelection from '../../Components/Navigation/Hooks/useRegionCommodityMenuSelectionHook';
import useLanguage from '../../Services/Language/useLanguageHook';
import useApi from './useApiHook';
import useApiWithoutAutoExecute from './useApiWithoutAutoExecute';

interface UseDashboardsApiResult {
    dashboards: DemeterDashboardModel[];
    isLoadingDashboards: boolean;
    isUpsertDemeterDashboardLoading: boolean;
    addPageElementToDashboard: (dashboardGuid: string, pageElement: DemeterPageElementModel) => Promise<void>;
    refreshDashboards: () => void;
}

// Shared state across all hook instances to prevent stale data
// Example: adding a page element to a dashboard from the projection then seasonal chart on the same page
let sharedDashboards: DemeterDashboardModel[] = [];
const sharedStateListeners: Array<(dashboards: DemeterDashboardModel[]) => void> = [];

const notifyAllListeners = (dashboards: DemeterDashboardModel[]) => {
    sharedDashboards = dashboards;
    sharedStateListeners.forEach((listener) => listener(dashboards));
};

const maximumNumberOfWidgetsPerRow = 2;
const useDashboardsApi = (): UseDashboardsApiResult => {
    const [translations] = useLanguage();
    const [dashboards, setDashboards] = useState<DemeterDashboardModel[]>(sharedDashboards);
    const [regionCommoditySelection] = useRegionCommodityMenuSelection();
    const [isLoadingDashboards, refreshDashboards, listDemeterDashboardsResponse] = useApi(() => demeterDashboardsApi.listDemeterDashboards(true));

    const [isUpsertDemeterDashboardLoading, upsertDemeterDashboard] = useApiWithoutAutoExecute(
        (request: UpsertDemeterDashboardRequest | undefined) => {
            if (!request) {
                return null;
            }

            return demeterDashboardsApi.upsertDemeterDashboard(request);
        },
        {
            successMessage: translations.dashboardCustomization.messages.updateDashboardSuccess,
            errorMessage: translations.dashboardCustomization.messages.updateDashboardError,
        },
    );
    const pendingDashboardsUpdatesReference = useRef(0);

    useEffect(() => {
        const listener = (updatedDashboards: DemeterDashboardModel[]) => {
            setDashboards(updatedDashboards);
        };
        sharedStateListeners.push(listener);

        return () => {
            const index = sharedStateListeners.indexOf(listener);
            if (index > -1) {
                sharedStateListeners.splice(index, 1);
            }
        };
    }, []);

    const addPageElementToDashboard = useCallback(
        async (dashboardGuid: string, pageElement: DemeterPageElementModel): Promise<void> => {
            const targetDashboard = dashboards.find((dashboard) => dashboard.demeterDashboardGuid === dashboardGuid);

            if (!targetDashboard) {
                return;
            }

            // In some TableDefinitions, mostly with stocks, extraParameters behave as subCommodities.
            // Ex: Stocks -> UnitedStates -> CrudeOil: extraParameters = 'All' vs extraParameters = 'DaysOfSupply'.
            const optionsForWidget: { [key: string]: string } = regionCommoditySelection?.extraParameters
                ? { extraParameters: regionCommoditySelection.extraParameters }
                : {};

            const newWidget: DemeterDashboardWidgetPageElementModel = {
                demeterDashboardWidgetPageElementGuid: applicationConstants.ZeroGuid,
                pageElement,
                options: optionsForWidget,
                order: 0,
            };
            const updatedRows = [...(targetDashboard.rows || [])];
            const lastRow = updatedRows[updatedRows.length - 1];
            if (lastRow && lastRow.rows?.length < maximumNumberOfWidgetsPerRow && !lastRow.layoutType) {
                newWidget.order = 1;
                const updatedLastRow = {
                    ...lastRow,
                    rows: [...lastRow.rows, newWidget],
                };
                updatedRows[updatedRows.length - 1] = updatedLastRow;
            } else {
                const newRow: DemeterDashboardWidgetModel = {
                    name: '',
                    order: targetDashboard.rows.length,
                    width: 0,
                    height: 0,
                    positionX: 0,
                    positionY: 0,
                    layoutType: undefined,
                    options: {},
                    rows: [newWidget],
                };
                updatedRows.push(newRow);
            }

            const upsertRequest: UpsertDemeterDashboardRequest = {
                demeterDashboardGuid: targetDashboard.demeterDashboardGuid,
                name: targetDashboard.name,
                order: targetDashboard.order,
                rows: updatedRows,
            };

            await upsertDemeterDashboard(upsertRequest);
            pendingDashboardsUpdatesReference.current += 1;
        },
        [dashboards, regionCommoditySelection],
    );

    useEffect(() => {
        if (isUpsertDemeterDashboardLoading || !pendingDashboardsUpdatesReference.current) {
            return;
        }

        refreshDashboards();
    }, [isUpsertDemeterDashboardLoading]);

    useEffect(() => {
        if (isLoadingDashboards || !listDemeterDashboardsResponse || !listDemeterDashboardsResponse.rows) {
            return;
        }

        notifyAllListeners(listDemeterDashboardsResponse.rows);
        pendingDashboardsUpdatesReference.current = 0;
    }, [listDemeterDashboardsResponse, isLoadingDashboards]);

    return {
        dashboards,
        isLoadingDashboards,
        isUpsertDemeterDashboardLoading,
        addPageElementToDashboard,
        refreshDashboards,
    };
};

export default useDashboardsApi;
